/**
 * Version management service.
 *
 * Handles frontend/backend version checking, update dialogs,
 * and native app version notifications.
 */

// NOTE: App, api, and platform flags are accessed lazily via (window as any)
// because app-compat.ts imports from this file, creating a circular dependency.
// Capturing them at module top-level would get `undefined`.

import { hasNumericValue } from '@/utils/validation';
import { getCurrentUserId } from '@/utils/auth';
import { getDirtyChecks } from '@/router/useUnsavedChangesBlocker';

// ── App version tracking ───────────────────────────────────────────

let appMajorVersion: number | null = null;
let appMinorVersion: number | null = null;
let appBuildVersion: number | null = null;

function isAtLeastVersionNumber(major: number, minor: number, build?: number): boolean {
  if (appMajorVersion === null || appMinorVersion === null) return false;
  if (appMajorVersion > major) return true;
  if (appMajorVersion < major) return false;
  if (build !== undefined) {
    if (appMinorVersion > minor) return true;
    if (appMinorVersion < minor) return false;
    return (appBuildVersion ?? 0) >= build;
  }
  return appMinorVersion >= minor;
}

export function appVersionCallback(versionNumber: string): void {
  if ((window as any).isAndroidApp) {
    if (versionNumber) {
      const versionNums = versionNumber.split('.');
      if (versionNums.length >= 2) {
        appMajorVersion = parseInt(versionNums[0]);
        appMinorVersion = parseInt(versionNums[1]);
      }
    }
  } else if ((window as any).isWindowsAppSdkApp) {
    if (versionNumber) {
      const versionNums = versionNumber.split('.');
      if (versionNums.length >= 3) {
        appMajorVersion = parseInt(versionNums[0]);
        appMinorVersion = parseInt(versionNums[1]);
        appBuildVersion = parseInt(versionNums[2]);
      }
    }
  } else if ((window as any).isIOSApp) {
    if (versionNumber) {
      const versionNums = versionNumber.split('.');
      if (versionNums.length >= 2) {
        appMajorVersion = parseInt(versionNums[0]);
        appMinorVersion = parseInt(versionNums[1]);
      }
    }
  }
}

// ── Version checking ──────────────────────────────────────────────

export function latestFrontendVersionCallback(callback: (latestVersion: number) => void): void {
  (window as any).api.frontendVersionCheck((result: string) => {
    const match = result.match(/\d+/);
    if (!match) return;
    const latestVersion: number = +match[0];
    callback(latestVersion);
  });
}

export function latestBackendVersionCallback(callback: (latestVersion: number) => void): void {
  (window as any).api.backendVersionCheck((result: string) => {
    const match = result.match(/\d+/);
    if (!match) return;
    const latestVersion: number = +match[0];
    callback(latestVersion);
  });
}

export function newFrontendVersionAvailableCallback(callback: (isAvailable: boolean) => void): void {
  latestFrontendVersionCallback((latestVersion: number) => {
    const currentVersion: number = (window as any).App.getCurrentFrontendVersion();
    if (currentVersion > 0 && latestVersion > 0 && currentVersion !== latestVersion) {
      callback(true);
    } else {
      callback(false);
    }
  });
}

export function newVersionCheck(): void {
  // Throttle: skip if a version-triggered reload happened less than 60s ago
  // in this session. Prevents infinite reload loops while still allowing
  // subsequent version updates within the same browser session.
  const lastReload = sessionStorage.getItem('_versionReloadTime');
  if (lastReload && Date.now() - parseInt(lastReload) < 60000) return;

  newFrontendVersionAvailableCallback((isNewFrontendVersionAvailable: boolean) => {
    if (isNewFrontendVersionAvailable) {
      sessionStorage.setItem('_versionReloadTime', Date.now().toString());
      (window as any).reloadPageSkipBrowserCache();
    }
  });
}

// ── Periodic version check ────────────────────────────────────────
//
// After 15 minutes of no interaction, check for a new version and show a dialog.
// If still no interaction 15 minutes after the dialog, auto-refresh the page.
// Any interaction clears the auto-refresh and restarts the inactivity timer.
// The dialog is shown at most once; if dismissed, no further prompts.

let _inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let _autoRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let _periodicVersionCheckSuppressed = false;
const _periodicCheckThresholdMs = 15 * 60 * 1000; // 15 minutes

export function resetPeriodicVersionCheckTimer(): void {
  if (_inactivityTimer !== null) clearTimeout(_inactivityTimer);
  if (_autoRefreshTimer !== null) {
    clearTimeout(_autoRefreshTimer);
    _autoRefreshTimer = null;
  }

  if (_periodicVersionCheckSuppressed) return;

  _inactivityTimer = setTimeout(onInactivityVersionCheck, _periodicCheckThresholdMs);
}

function onInactivityVersionCheck(): void {
  _inactivityTimer = null;

  // Don't show if user has unsaved changes — let route-change check handle it
  const hasUnsavedChanges = getDirtyChecks().some(c => c.isActive() && c.isDirty())
    || (window as any).citationFormState?.isDirty;
  if (hasUnsavedChanges) return;

  newFrontendVersionAvailableCallback((isNewVersionAvailable: boolean) => {
    if (!isNewVersionAvailable) return;

    _periodicVersionCheckSuppressed = true;

    // Auto-refresh if no interaction for another 15 minutes
    _autoRefreshTimer = setTimeout(() => {
      (window as any).reloadPageSkipBrowserCache();
    }, _periodicCheckThresholdMs);

    (window as any).kstConfirm({
      title: 'Orna Update Available',
      messageHtml: 'A new version of Orna is available. Please refresh the page to get it. You may press Cancel to continue working and refresh later.',
      confirmText: 'Refresh Now',
      cancelText: 'Cancel',
      confirmClass: 'primary'
    }).then((confirmed: boolean) => {
      if (confirmed) (window as any).reloadPageSkipBrowserCache();
      // If cancelled, auto-refresh timer still runs but will be cleared on next interaction
    });
  });
}

// ── Version update dialog ─────────────────────────────────────────

export function showVersionUpdateDialog(): void {
  newFrontendVersionAvailableCallback((isNewVersionAvailable: boolean) => {
    if (isNewVersionAvailable) {
      (window as any).kstConfirm({
        title: 'Orna Update Required',
        messageHtml: 'A new version of Orna is available. Please refresh the page to get it. You may press Cancel below to save your work before you refresh the page.',
        confirmText: 'Refresh Now',
        cancelText: 'Cancel',
        confirmClass: 'primary'
      }).then((confirmed: boolean) => {
        if (confirmed) (window as any).reloadPageSkipBrowserCache();
      });
    }
  });
}

// ── Version updated notification ──────────────────────────────────

export function ornaVersionUpdatedNotification(): void {
  const currentFrontendVersionNumber: number = (window as any).App.getCurrentFrontendVersion();
  const previousFrontendVersionNumber: string | null = localStorage.getItem("FrontendVersionNumber");
  if (!previousFrontendVersionNumber) {
    localStorage.setItem("FrontendVersionNumber", currentFrontendVersionNumber.toString());
    return;
  }
  if (parseInt(previousFrontendVersionNumber) === currentFrontendVersionNumber) {
    return;
  }
  localStorage.setItem("FrontendVersionNumber", currentFrontendVersionNumber.toString());

  const location: string = window.location.href;
  if (location && (location.match('sectionName') || window.location.pathname.length > 1)) {
    (window as any).App.showPopupNotification("Orna has auto-updated to the latest version. No action needed.", "success");
  }
}

// ── Native app version notifications ──────────────────────────────

export function notifyAndroidVersionUpdate(): void {
  if ((window as any).isAndroidApp && isAtLeastVersionNumber(1, 17)) {
    return;
  }

  if (!hasNumericValue(getCurrentUserId())) {
    return;
  }

  (window as any).App.showPopupNotification("Please update Orna from Google Play. This older version is no longer supported. You may also log in from orna.kerkton.com in Chrome.", "error", 150);
  (window as any).api.insertNewJSError("Using Android version build < 17. Version is " + appMajorVersion + "." + appMinorVersion, null, null, (window as any).JSErrorType.CAUGHT, null);

  setTimeout(() => {
    (window as any).App.signOut();
  }, 10000);
}

export function notifyWindowsAppSdkAppVersionUpdate(): void {
  if ((window as any).isWindowsAppSdkApp && isAtLeastVersionNumber(2, 0, 5)) {
    return;
  }
  (window as any).App.showPopupNotification("Please update Orna from Microsoft Store.", "error", 150);
  (window as any).api.insertNewJSError("Using old Windows App SDK app version.", null, null, (window as any).JSErrorType.CAUGHT, null);
}

// ── Register on window ────────────────────────────────────────────

// Expose version variables on window so legacy isAtLeastVersionNumber() in Utils.ts can read them
Object.defineProperty(window, 'appMajorVersion', {
  get: () => appMajorVersion,
  set: (v) => { appMajorVersion = v; },
  configurable: true,
});
Object.defineProperty(window, 'appMinorVersion', {
  get: () => appMinorVersion,
  set: (v) => { appMinorVersion = v; },
  configurable: true,
});
Object.defineProperty(window, 'appBuildVersion', {
  get: () => appBuildVersion,
  set: (v) => { appBuildVersion = v; },
  configurable: true,
});

(window as any).appVersionCallback = appVersionCallback;
(window as any).notifyAndroidVersionUpdate = notifyAndroidVersionUpdate;
(window as any).notifyWindowsAppSdkAppVersionUpdate = notifyWindowsAppSdkAppVersionUpdate;
(window as any).ornaVersionUpdatedNotification = ornaVersionUpdatedNotification;
